import { Cart, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getProductUrlSync } from '@msdyn365-commerce-modules/retail-actions';
import { ICoreContext, Image, getUrlSync, IImageSettings } from '@msdyn365-commerce/core';
import { ImageFetchPriority } from '@msdyn365-commerce/core-internal';
import { format } from '@msdyn365-commerce-modules/utilities';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PriceComponent from '../../themes/defa-theme/views/components/price.component';
import { ProductListProduct } from '../product-list-component/product-list-component';
import classnames from 'classnames';
import { useOrganizationHidePrices } from '../../shared-utils/hooks/use-organization-hide-prices';

export interface ICartPopupProductAndQuantity {
    product: SimpleProduct | ProductListProduct;
    quantity: number;
}

export interface ICartPopupProps {
    context: ICoreContext;
    cart: Cart | undefined;
    products: ICartPopupProductAndQuantity[];
    autoCloseMs?: number;
    onClose: () => void;
    resetKey: number;
    subTotalLabel?: string;
    goToCartButtonLabel?: string;
    moreProductsLabel?: string;
}

let host: HTMLElement | null = null;
let autoCloseTimer: number | null = null;

function ensureHost() {
    if (host) {
        return;
    }
    host = document.createElement('div');
    host.id = 'popup-host';
    document.body.appendChild(host);
}

function unmount() {
    if (!host) {
        return;
    }
    ReactDOM.unmountComponentAtNode(host);
    if (autoCloseTimer) {
        clearTimeout(autoCloseTimer);
        autoCloseTimer = null;
    }
}

const ProductImage = ({ context, baseClassName, product, productUrl }: { context: ICoreContext; baseClassName: string; product: SimpleProduct | ProductListProduct; productUrl: string }) => {
    const width = 64;
    const height = 64;
    const defaultImageSettings: IImageSettings = {
        viewports: {
            xs: { w: width, h: height, q: `w=${width}&h=${height}&q=80&m=0&f=webp`, qe: `t=${width}h${height}qhigh` },
            lg: { w: width, h: height, q: `w=${width}&h=${height}&q=80&m=0&f=webp`, qe: `t=${width}h${height}qhigh` },
            xl: { w: width, h: height, q: `w=${width}&h=${height}&q=80&m=0&f=webp`, qe: `t=${width}h${height}qhigh` }
        },
        disableLazyLoad: false,
        lazyload: true,
        quality: 80
    };

    const contextConfig = context.request.app.config?.placeholderImageName;
    const emptyPlaceHolderImage = contextConfig as string;
    let fallbackImageSource = context?.request.apiSettings.baseImageUrl;
    if (fallbackImageSource?.endsWith('/')) {
        fallbackImageSource = fallbackImageSource.slice(0, -1);
    }
    if (emptyPlaceHolderImage) {
        fallbackImageSource = `${fallbackImageSource}${emptyPlaceHolderImage}`;
    }

    return (
        <a href={productUrl} className={`${baseClassName}__product-image-link`} aria-label={product.Name || 'Product image'}>
            <Image
                requestContext={context.actionContext.requestContext}
                className={`${baseClassName}__product-image`}
                altText={product.Name || ''}
                src={product.PrimaryImageUrl || fallbackImageSource}
                fallBackSrc={fallbackImageSource}
                gridSettings={context.request.gridSettings!}
                imageSettings={defaultImageSettings}
                loadFailureBehavior='empty'
                fetchPriority={ImageFetchPriority.High}
                tabIndex={0}
                height={height}
                width={width}
            />
        </a>
    );
};

const CartPopup = ({
    context,
    cart,
    products,
    autoCloseMs,
    onClose,
    resetKey,
    subTotalLabel,
    goToCartButtonLabel,
    moreProductsLabel
}: ICartPopupProps) => {
    const baseClassName = 'cart-popup';
    const maxProductsToShow = 3;
    const hidePrices = useOrganizationHidePrices(context);

    const [progress, setProgress] = useState(100);
    const [headerBottom, setHeaderBottom] = useState(0);
    const [isNavigating, setIsNavigating] = useState(false);

    const rafId = useRef<number | null>(null);
    const paused = useRef(false);
    const remainingMs = useRef<number>(autoCloseMs || 0);
    const lastNow = useRef<number>(0);

    // Hover handlers to pause/resume
    const handleMouseEnter = () => {
        if (isNavigating) {
            return;
        }
        paused.current = true;
    };
    const handleMouseLeave = () => {
        if (isNavigating) {
            return;
        }
        paused.current = false;
        // realign timing right when resuming
        lastNow.current = performance.now();
    };

    useEffect(() => {
        const handleScroll = () => {
            const header = document.querySelectorAll('.ms-header')?.[0];
            if (header) {
                const { bottom } = header.getBoundingClientRect();
                setHeaderBottom(bottom);
            }
        };
        handleScroll();

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useEffect(() => {
        // Reset countdown every time resetKey changes or autoCloseMs changes
        if (!autoCloseMs || autoCloseMs <= 0) {
            setProgress(100);
            return;
        }

        remainingMs.current = autoCloseMs;
        setProgress(100);
        paused.current = false;
        lastNow.current = performance.now();

        const tick = (now: number) => {
            if (!paused.current && !isNavigating) {
                const delta = now - lastNow.current; // ms since last frame
                remainingMs.current = Math.max(0, remainingMs.current - delta);
                const pct = (remainingMs.current / autoCloseMs) * 100;
                setProgress(pct);
            }
            lastNow.current = now;

            if (remainingMs.current > 0) {
                rafId.current = requestAnimationFrame(tick);
            } else {
                onClose();
            }
        };

        rafId.current = requestAnimationFrame(tick);

        return () => {
            if (rafId.current) {
                cancelAnimationFrame(rafId.current);
            }
            rafId.current = null;
        };
    }, [autoCloseMs, resetKey]);

    const subTotal = cart?.CartLines?.reduce((sum, line) => sum + (line.Price || 0) * (line.Quantity || 1), 0) || 0;

    return (
        <div className={baseClassName} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} style={{ top: Math.max(headerBottom, 0) + 30 }}>
            <button className={`${baseClassName}__close-button`} onClick={onClose} title='Close' />

            <div className={`${baseClassName}__product-container`}>
                <ul className={`${baseClassName}__product-list`}>
                    {products.slice(0, maxProductsToShow).map((prod, index) => {
                        return (
                            <li key={index} className={`${baseClassName}__product-item`}>
                                <div style={{ display: 'flex', gap: 10 }}>
                                    <ProductImage context={context} baseClassName={baseClassName} product={prod.product} productUrl={getProductUrlSync(prod.product as SimpleProduct, context.actionContext)} />
                                    <a href={getProductUrlSync(prod.product as SimpleProduct, context.actionContext)} className={`${baseClassName}__product-name`}>
                                        {prod.quantity} x {prod.product.Name}
                                    </a>
                                </div>
                            </li>
                        );
                    })}
                    {products.length > maxProductsToShow && (
                        <li className={`${baseClassName}__product-item ${baseClassName}__product-item--more`}>
                            {format(moreProductsLabel || 'and {0} more...', products.length - maxProductsToShow)}
                        </li>
                    )}
                </ul>
            </div>

            <div className={`${baseClassName}__cart-summary`}>
                {cart !== undefined && !hidePrices && (
                    <div className={`${baseClassName}__subtotal-container`}>
                        <span className={`${baseClassName}__subtotal-label`}>{subTotalLabel || 'Subtotal'}:&nbsp;</span>
                        <PriceComponent
                            className={`${baseClassName}__subtotal-value`}
                            context={context}
                            data={{ price: { CustomerContextualPrice:subTotal } }}
                            id='cart-popup__0'
                            typeName='cart-popup'
                        />
                    </div>
                )}
                <a
                    className={classnames(`${baseClassName}__cart-button`, { loading: isNavigating })}
                    data-disabled={isNavigating}
                    href={getUrlSync('cart', context.actionContext)}
                    onClick={(e) => {
                        setIsNavigating(true);

                        // Fallback in case navigation is canceled or fails, to re-enable the popup
                        setTimeout(() => {
                            setIsNavigating(false);
                        }, 5000);
                    }}
                >
                    {goToCartButtonLabel || 'To cart'}
                </a>
            </div>

            {autoCloseMs && (
                <div className={`${baseClassName}__progress-bar-container`}>
                    <div className={`${baseClassName}__progress-bar`} style={{ ['--progress' as any]: `${progress}%` }} />
                </div>
            )}
        </div>
    );
};

export function showCartPopup(
    context: ICoreContext,
    cart: Cart | undefined,
    products: ICartPopupProductAndQuantity[],
    subTotalLabel?: string,
    goToCartButtonLabel?: string,
    moreProductsLabel?: string,
    autoCloseMs?: number
) {
    ensureHost();

    ReactDOM.render(
        <CartPopup
            context={context}
            cart={cart}
            products={products}
            autoCloseMs={autoCloseMs}
            onClose={unmount}
            // new key each call => resets the timer even if product is identical
            resetKey={Date.now()}
            subTotalLabel={subTotalLabel}
            goToCartButtonLabel={goToCartButtonLabel}
            moreProductsLabel={moreProductsLabel}
        />,
        host
    );
}
